import { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import { MapPin, Filter } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Child component that uses the Leaflet map instance to add a marker cluster
 * layer. React-leaflet v5 + React 19 strict mode compatible.
 */
function ClusterLayer({ vendors }) {
  const map = useMap();

  useEffect(() => {
    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 50,
      showCoverageOnHover: false,
    });

    const tierColor = (m) => ({ elite: '#F59E0B', premium: '#8B5CF6', standard: '#3B82F6' }[m] || '#3B82F6');

    vendors.forEach((v) => {
      const color = tierColor(v.membership_level);
      const radius = v.membership_level === 'elite' ? 9 : 7;
      const marker = L.circleMarker([v.service_lat, v.service_lng], {
        radius,
        color,
        fillColor: color,
        fillOpacity: 0.85,
        weight: 2,
      });
      const html = `
        <div style="min-width:220px;font-family:system-ui,sans-serif" data-testid="map-popup-${v.vendor_id}">
          <div style="font-weight:700;font-size:13px;margin-bottom:4px;color:#111">${(v.business_name || v.owner_name || '').replace(/</g, '&lt;')}</div>
          <div style="font-size:11px;color:#555;margin-bottom:6px">📍 ${(v.service_city || '—')}, ${(v.service_state || '')}${v.service_radius_miles ? ` · ${v.service_radius_miles} mi radius` : ''}</div>
          ${v.rating > 0 ? `<div style="font-size:11px;color:#b45309;margin-bottom:6px">★ ${v.rating} <span style="color:#999">(${v.review_count || 0} reviews)</span></div>` : ''}
          ${v.tribal_affiliation ? `<div style="font-size:11px;color:#6d28d9;margin-bottom:6px">🪶 ${v.tribal_affiliation}</div>` : ''}
          <a href="/vendor/${v.vendor_id}" style="display:inline-block;margin-top:4px;padding:5px 12px;border-radius:6px;background:#7c3aed;color:#fff;font-size:11px;font-weight:600;text-decoration:none">View Profile →</a>
        </div>`;
      marker.bindPopup(html);
      cluster.addLayer(marker);
    });

    map.addLayer(cluster);
    return () => {
      map.removeLayer(cluster);
    };
  }, [map, vendors]);

  return null;
}

/**
 * Public vendor map — US + Canada view with city-level pins.
 * Uses Leaflet + OpenStreetMap tiles (no API key required).
 * Pins are jittered server-side so multiple vendors in the same city don't
 * overlap but never reveal an exact address.
 */
export default function VendorMapView({ categoryId = 'all' }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState(categoryId);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetch(`${API}/categories`).then((r) => r.ok && r.json()).then((d) => setCategories(d || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = `${API}/vendors/map-data${categoryFilter && categoryFilter !== 'all' ? `?category_id=${categoryFilter}` : ''}`;
    fetch(url)
      .then((r) => r.ok ? r.json() : { vendors: [] })
      .then((d) => setVendors(d.vendors || []))
      .catch(() => setVendors([]))
      .finally(() => setLoading(false));
  }, [categoryFilter]);

  const cityStats = useMemo(() => {
    const m = {};
    for (const v of vendors) {
      const key = `${v.service_city || ''}, ${v.service_state || ''}`;
      m[key] = (m[key] || 0) + 1;
    }
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [vendors]);

  return (
    <div className="relative w-full" data-testid="vendor-map-view">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 px-4 sm:px-0">
        <div className="flex items-center gap-2 text-[#9CA3AF] text-sm">
          <Filter size={14} />
          <span>Category:</span>
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-1.5 min-w-[160px]"
          data-testid="map-category-filter"
        >
          <option value="all">All Categories</option>
          {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
        </select>
        <div className="text-[#9CA3AF] text-xs ml-auto" data-testid="map-vendor-count">
          {loading ? 'Loading…' : `${vendors.length} vendor${vendors.length === 1 ? '' : 's'} on map`}
        </div>
      </div>

      {cityStats.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 px-4 sm:px-0">
          {cityStats.map(([city, count]) => (
            <span key={city} className="px-3 py-1 rounded-full bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 text-[#C4B5FD] text-xs">
              <MapPin size={10} className="inline mr-1" />{city} ({count})
            </span>
          ))}
        </div>
      )}

      <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.3)] relative" style={{ height: '70vh', minHeight: '500px' }}>
        <MapContainer
          center={[42.5, -96]}
          zoom={4}
          minZoom={3}
          maxZoom={12}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          <ZoomControl position="bottomright" />
          <ClusterLayer vendors={vendors} />
        </MapContainer>

        {/* Tier legend */}
        <div className="absolute bottom-4 left-4 z-[400] bg-black/70 backdrop-blur-xl border border-white/10 rounded-lg p-3 text-[10px] text-white space-y-1">
          <div className="font-semibold mb-1 text-[#A78BFA]">Membership tier</div>
          <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Elite</div>
          <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#8B5CF6]" /> Premium</div>
          <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" /> Standard</div>
        </div>
      </div>

      <p className="text-[#64748B] text-[10px] mt-3 text-center">
        Pins show the vendor's service city only — exact addresses are never displayed.
      </p>
    </div>
  );
}
